// Public Violation Report Image Upload Module
// Ported from app/javascript/packs/code_enforcement/form/form.js (CodeEnforcementImageUpload +
// initializeImageUploadHandlers) so the public complaint form's "Take Photo" button behaves
// exactly like the Code Enforcement dashboard's capture button. The only real differences are:
//   - the reporter isn't signed in, so this hits the public equivalents of
//     HealthInspectionsController#get_location / #extract_image_info, scoped to the current
//     organization via the page's own path (/violation-reports/:subdomain)
//   - there's no cocoon "add image row" UI here, just one multi-file input, so the change
//     handler appends flat image_locations[]/image_timestamps[] fields and a metadata list
//     instead of building a nested-fields image card
const PublicViolationReportImageUpload = (function() {
    // Private state - scoped to this module only
    let imageState = { capture: false, lat: null, long: null };

    // unlike CE's fixed /health_inspections/... urls, these are organization-scoped
    // (/violation-reports/:subdomain/...) and set once from the form's data attributes
    let endpoints = { extractImageInfoUrl: null, getLocationUrl: null };

    function formatTimestamp(date = new Date()) {
        let time = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        }).toLowerCase();

        let fullDate = date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
        });

        let weekday = date.toLocaleDateString("en-US", {
            weekday: "long"
        });

        return `${time} | ${fullDate} | ${weekday}`;
    }

    async function getLocation() {
        try {
            const data = await $.ajax({
                url: endpoints.getLocationUrl,
                data: { lat: imageState.lat, long: imageState.long },
                method: "GET",
                dataType: "json"
            });

            if (data) {
                return { success: true, location: data.location || 'Location not found'};
            } else {
                return { success: false, error: "Invalid response format" };
            }
        } catch (e) {
            return { success: false, error: "Failed to get location" };
        }
    }

    async function extractImageInfo(file) {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const data = await $.ajax({
                url: endpoints.extractImageInfoUrl,
                type: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                dataType: 'json',
                headers: {
                    'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content')
                }
            });

            if (data && data.metadata) {
                return { success: true, ...data.metadata };
            } else {
                return { success: false, date_time_original: null, date_time_original_iso: null, location: null };
            }
        } catch (e) {
            return { success: false, date_time_original: null, date_time_original_iso: null, location: null };
        }
    }

    // Get metadata (timestamp and location) for the image
    async function getImageMetadata(imageInfo) {
        let timestamp, timestampDisplay, location;

        if (imageInfo.date_time_original_iso) {
            timestamp = imageInfo.date_time_original_iso;
            timestampDisplay = imageInfo.date_time_original || null;
            location = imageInfo.location || null;
        } else if (imageState.capture === false) {
            timestamp = null;
            timestampDisplay = null;
            location = imageInfo.location || null;
        } else {
            // Captured image - use current time and device location
            const now = new Date();
            timestamp = now.toISOString();
            timestampDisplay = formatTimestamp(now);

            const locationData = await getLocation();
            location = locationData.success ? locationData.location : null;
        }

        return {
            timestamp: timestamp,
            timestampDisplay: timestampDisplay,
            location: location
        };
    }

    function requestLocation() {
        if (!("geolocation" in navigator)) {
            console.error("Geolocation is not supported by your browser.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                imageState.lat = position.coords.latitude;
                imageState.long = position.coords.longitude;

                $('#pvrLocationAccessModalHeader').text('Location Access Granted');
                $('#pvrLocationAccessModalBody').html('<p>Location access has been granted. Location will be tagged for the captured photo only.</p>');
            },
            (error) => {
                console.error("Error getting location:", error.message);
                $('#pvrLocationAccessModalHeader').text('Location Access Denied');
                $('#pvrLocationAccessModalBody').html('<p>Location access is required. Please enable it in your browser or device settings.</p><p>Without location access there will be no location information on the photo.</p><p>However you can continue taking pictures.</p><a href="https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DAndroid&oco=0" target=_blank style="text-decoration: none">How to enable location?</a>');
                $('#pvrLocationAccessModal').modal('show');
            }
        );
    }

    // Public API
    return {
        getState: function() { return imageState; },
        setState: function(key, value) { imageState[key] = value; },
        setEndpoints: function(urls) { endpoints = urls; },
        requestLocation: requestLocation,
        extractImageInfo: extractImageInfo,
        getImageMetadata: getImageMetadata
    };
})();

function initializePvrImageUploadHandlers(form) {
    PublicViolationReportImageUpload.setEndpoints({
        extractImageInfoUrl: $(form).data('extractImageInfoUrl'),
        getLocationUrl: $(form).data('getLocationUrl')
    });

    const filesField = $(form).find('#pvrFilesField');
    const uploadBtn = $(form).find('#pvrChooseFilesBtn');
    const captureBtn = $(form).find('#pvrCapturePhotoBtn');
    const metadataFieldsContainer = $(form).find('#pvrImageMetadataFields');
    const metadataListContainer = $(form).find('#pvrImageMetadataList');

    // a native file input's FileList is replaced (not merged) by every new selection,
    // so taking a second photo would otherwise drop the first one. CE avoids this by
    // giving every photo its own nested-fields row that never gets touched again; here
    // there's one flat input, so instead these arrays accumulate every photo taken or
    // chosen so far, and the input's .files is rebuilt from them after each change
    let accumulatedFiles = [];
    let accumulatedMetadata = [];

    function syncFilesFieldWithAccumulatedFiles() {
        const dataTransfer = new DataTransfer();
        accumulatedFiles.forEach((file) => dataTransfer.items.add(file));
        filesField[0].files = dataTransfer.files;
    }

    function removeAccumulatedFile(index) {
        accumulatedFiles.splice(index, 1);
        accumulatedMetadata.splice(index, 1);
        syncFilesFieldWithAccumulatedFiles();
        renderAccumulatedFiles();
    }

    function renderAccumulatedFiles() {
        metadataFieldsContainer.empty();
        metadataListContainer.empty();

        accumulatedFiles.forEach((file, index) => {
            const metadata = accumulatedMetadata[index];

            $('<input>', { type: 'hidden', name: 'public_violation_report[image_locations][]', value: metadata.location || '' }).appendTo(metadataFieldsContainer);
            $('<input>', { type: 'hidden', name: 'public_violation_report[image_timestamps][]', value: metadata.timestamp || '' }).appendTo(metadataFieldsContainer);

            const row = $('<div class="d-flex align-items-start justify-content-between gap-2 mb-2"></div>');
            const details = $('<div class="text-muted small"></div>');
            details.append($('<div></div>').text(file.name));

            if (metadata.timestampDisplay) {
                details.append($('<div></div>').text(`Captured: ${metadata.timestampDisplay}`));
            }
            if (metadata.location) {
                details.append($('<div></div>').text(`Location: ${metadata.location}`));
            }
            if (!metadata.timestampDisplay && !metadata.location) {
                details.append($('<div></div>').text('No location or time data found in this photo.'));
            }

            const removeBtn = $('<button type="button" class="btn-close flex-shrink-0" aria-label="Remove photo"></button>');
            removeBtn.on('click', function () { removeAccumulatedFile(index); });

            row.append(details).append(removeBtn);
            metadataListContainer.append(row);
        });
    }

    // reattach events
    uploadBtn.off('click');
    captureBtn.off('click');
    filesField.off('change');

    uploadBtn.on('click', function() {
        PublicViolationReportImageUpload.setState('capture', false);
        filesField.removeAttr('capture');
        filesField.trigger('click');
    });

    captureBtn.on('click', function() {
        PublicViolationReportImageUpload.setState('capture', true);
        filesField.attr('capture', 'environment');

        if ('permissions' in navigator) {
            navigator.permissions.query({ name: 'geolocation' }).then(result => {
                if (result.state === 'granted') {
                    PublicViolationReportImageUpload.requestLocation();
                    filesField.trigger('click');
                } else if (result.state === 'prompt') {
                    PublicViolationReportImageUpload.requestLocation();
                    $('#pvrLocationAccessModalHeader').text('Location Access Required');
                    $('#pvrLocationAccessModalBody').html('<p>This app would like to access your location to:</p><ul><li>Add location information to your photo</li><li>Create a timestamp with coordinates</li></ul><p>Your privacy matters: Location data will only be used for photo tagging and will not be shared with third parties.</p>');
                    $('#pvrLocationAccessModal').modal('show');
                    $('#pvrLocationAccessModal').data('fileField', filesField);
                } else if (result.state === 'denied') {
                    $('#pvrLocationAccessModalHeader').text('Location Access Denied');
                    $('#pvrLocationAccessModalBody').html('<p>Location access is required. Please enable it in your browser or device settings.</p><p>Without location access there will be no location information on the photo.</p><p>However you can continue taking pictures.</p><a href="https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DAndroid&oco=0" target=_blank style="text-decoration: none">How to enable location?</a>');
                    $('#pvrLocationAccessModal').modal('show');
                    $('#pvrLocationAccessModal').data('fileField', filesField);
                }
            });
        } else {
            PublicViolationReportImageUpload.requestLocation();
            filesField.trigger('click');
        }
    });

    filesField.on('change', async function(evt) {
        const newFiles = Array.from(evt.target.files || []);
        if (newFiles.length === 0) return;

        const loadingIndicator = $('<div class="text-muted small">Reading photo details…</div>');
        metadataListContainer.append(loadingIndicator);

        try {
            for (const file of newFiles) {
                const imageInfo = await PublicViolationReportImageUpload.extractImageInfo(file);
                const metadata = await PublicViolationReportImageUpload.getImageMetadata(imageInfo);

                accumulatedFiles.push(file);
                accumulatedMetadata.push(metadata);
            }
        } catch (error) {
            console.error('Error processing images:', error);
        } finally {
            loadingIndicator.remove();
            PublicViolationReportImageUpload.setState('capture', false);
            // setting .files here does not itself fire another change event, so this is safe
            syncFilesFieldWithAccumulatedFiles();
            renderAccumulatedFiles();
        }
    });
}

document.addEventListener('turbolinks:load', function () {
    if ($('.violation_reports.new, .violation_reports.create').length === 0) {
        return false;
    }

    const form = document.getElementById('violation-report-form');
    if (!form) return;

    initializePvrImageUploadHandlers(form);

    $('#pvr-go-to-capture').on('click', function() {
        $('#pvrLocationAccessModal').modal('hide');
        let $fileField = $('#pvrLocationAccessModal').data('fileField');
        if ($fileField) {
            $fileField.trigger('click');
        }
    });
});
